import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DialogService } from '../dialog/dialog.service';
import type { AddConnectionDialogData } from '../../../components/add-connection-dialog/add-connection-dialog.component';

/** Minimal shape returned by the add-connection dialog on success. */
interface CreatedConnectionResult {
  connection?: {
    name?: string;
    connectionId?: number;
    projectId?: number;
  };
}

/**
 * Owns the multi-step "add a connection" flow so every surface runs it
 * identically: open the add-connection dialog, and on success offer the
 * success dialog with an optional "start testing" step. Previously this exact
 * chain was duplicated in project-info-sidebar and connections-list-dialog.
 *
 * Returns an observable that emits `true` once a connection was created and the
 * flow finished (so the caller can refresh its list), or `false` if the user
 * cancelled before creating anything.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionFlowService {
  readonly #dialogService = inject(DialogService);

  addConnection(projectId: number): Observable<boolean> {
    const dialogData: AddConnectionDialogData = { projectId };
    return this.#dialogService.addConnectionDialog(dialogData).pipe(
      switchMap((result) => {
        const created = (result as CreatedConnectionResult | undefined)?.connection;
        if (!created) {
          return of(false);
        }

        const connectionName = created.name ?? 'Unknown';
        const resultProjectId = Number(created.projectId ?? projectId);
        const connectionId = created.connectionId ?? 0;

        return this.#dialogService.connectionSuccessDialog(connectionName).pipe(
          switchMap((action) => {
            if (action === 'start-testing' && connectionId && resultProjectId) {
              return this.#dialogService
                .connectionTestingDialog({
                  connectionName,
                  projectId: resultProjectId,
                  connectionId,
                })
                .pipe(map(() => true));
            }
            return of(true);
          }),
        );
      }),
    );
  }
}
